import { useState } from 'react';
import { useGT } from 'gt-react';
import { Autocomplete } from '@/components/common/inputs/autocomplete';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import { useSearchProductVariants } from '@/api/inventory-management/products/useSearchProductVariants';

interface VariantOption {
  id: string;
  label: string;
  productName: string;
}

/**
 * Product-variant combobox (Discounts v2): a persistent selector — the chosen
 * SKU stays in the trigger and reopens the search — with the dropdown rendered
 * in a portal so it is never clipped by scroll/overflow containers (same
 * Autocomplete the classification pickers use). Shared by the create-group
 * dialog and the group review screen.
 */
export function VariantPicker({
  value,
  label,
  description,
  onSelect,
  placeholder,
}: {
  /** Selected variant id ('' when none). */
  value: string;
  /** Display label for the selected variant (SKU or product name). */
  label: string;
  /** Product name shown truncated under the trigger (controlled by parent). */
  description?: string;
  onSelect: (variantId: string, label: string, description?: string) => void;
  placeholder?: string;
}) {
  const t = useGT();
  const [query, setQuery] = useState('');
  // Product name of the current selection, shown truncated under the trigger
  // (the trigger itself stays SKU-only so long names can't blow the layout).
  const [selectedDescription, setSelectedDescription] = useState('');
  const { data: results, isLoading } = useSearchProductVariants({
    query,
    limit: 8,
  });

  const options: VariantOption[] = results.map((r) => ({
    id: r.productVariantId,
    label: r.variantSku ?? r.productName,
    productName: r.productName,
  }));
  // Name the selection even when it isn't in the current results.
  const selected: VariantOption[] | undefined =
    value && !options.some((o) => o.id === value)
      ? [{ id: value, label: label || '…', productName: '' }]
      : undefined;

  return (
    <div className="min-w-0">
      <Autocomplete
        deselectable
        allowClear
        items={options}
        selectedItems={selected}
        onSearch={(next) => setQuery(next)}
        debounceMs={0}
        getValue={(option) => option.id}
        getLabel={(option) => option.label}
        indicator="none"
        renderItem={(option) => (
          <span className="min-w-0">
            <span className="font-mono text-xs">{option.label}</span>
            {option.productName && option.productName !== option.label ? (
              <span className="ml-1 text-xs text-muted-foreground">
                {option.productName}
              </span>
            ) : null}
          </span>
        )}
        value={value || null}
        onValueChange={(id, picked) => {
          const pickedDescription =
            picked && picked.productName !== picked.label
              ? picked.productName
              : '';
          setSelectedDescription(pickedDescription);
          onSelect(id ?? '', picked?.label ?? '', pickedDescription);
        }}
        loading={isLoading && query.trim().length > 0}
        labels={{
          ...pickerLabels({
            placeholder: placeholder ?? t('Search SKU…'),
            search: t('Search by SKU or product name…'),
            empty:
              query.trim().length === 0
                ? t('Type to search products.')
                : t('No products found.'),
          }),
          loading: t('Searching…'),
        }}
        className="w-full"
      />
      {value && (description ?? selectedDescription) ? (
        <div
          className="mt-0.5 truncate text-[10px] text-muted-foreground"
          title={description ?? selectedDescription}
        >
          {description ?? selectedDescription}
        </div>
      ) : null}
    </div>
  );
}
